import React, {useState, useRef, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import './ShortUrlModal.scss'
import Select from 'react-select'
import {ENV} from '../../../../Utils/_helpers/EnvHelper'
import {useDispatch, useSelector} from 'react-redux'
import {
  getAllUserDetails,
  getDefaultShortUrl,
} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {BulkMsgDraftData} from '../../new-message/BulkMessageSlice/BulkMessageSlice'
import {Link} from 'react-router-dom'
import {ThunkDispatch} from 'redux-thunk'
import {AnyAction} from 'redux'
import {CreateData, getAllTrackingLink} from '../../reports-analytics/ShortLinkSlice/ShortLinkSlice'
import {InputMessageContentRef} from '../../../../Components/types/InputMessageContent'

interface ShortUrlModalProps {
  inputRef: React.MutableRefObject<InputMessageContentRef | null>
  setBulkMessageData: (data: unknown) => void
  bulkMessageData: any
  isShow: boolean
  onClose: () => void
  isDraft?: boolean
  initialData?: {
    longUrl: string
    shortUrl: string
  }
}

const ShortUrlModal: React.FC<ShortUrlModalProps> = ({
  inputRef,
  setBulkMessageData,
  bulkMessageData,
  isShow,
  onClose,
  isDraft = false,
  initialData,
}) => {
  const dispatch = useDispatch()

  const defaultDomain = useSelector(getDefaultShortUrl)

  const userValues = useSelector(getAllUserDetails)
  const thunkDispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>()
  const allTrackingLinks = useSelector(getAllTrackingLink)

  const [destinationUrl, setDestinationUrl] = useState('')
  const [shortUrl, setShortUrl] = useState('')
  const [domain, setDomain] = useState(defaultDomain)
  const [selectedDomain, setSelectedDomain] = useState({value: '', label: defaultDomain})
  const [isSuccess, setIsSuccess] = useState(false)
  const [urlEnding, setUrlEnding] = useState('')
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [errors, setErrors] = useState({destinationUrl: '', shortUrl: ''})
  const [domainOptions, setDomainOptions] = useState([
    {
      value: 'create',
      label: '+ Add Custom Domain',
    },
  ])
  const shortUrlRef = useRef<HTMLInputElement>(null)
  const urlEndingRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (initialData?.longUrl && initialData.longUrl !== '') {
      setDestinationUrl(initialData.longUrl.trim())
      setShortUrl(initialData.shortUrl?.trim() || '')
    } else {
      setDestinationUrl('')
      setShortUrl('')
    }
  }, [initialData])

  const handleClose = () => {
    onClose()
    setDomain(defaultDomain)
    setSelectedDomain({value: '', label: defaultDomain})
    inputRef?.current?.focus()
  }

  // Reset form or populate with initial data when modal opens
  useEffect(() => {
    if (isShow) {
      setIsSuccess(false)
      if (initialData) {
        setDestinationUrl(initialData.longUrl)
        setShortUrl(initialData.shortUrl)
        // Parse shortUrl to extract domain and ending if needed
        if (initialData.shortUrl && initialData.shortUrl.includes('/')) {
          const parts = initialData.shortUrl.split('/')
          setDomain(parts[0])
          setUrlEnding(parts[1] || '')
        } else {
          setUrlEnding(initialData.shortUrl)
        }
      } else {
        setDestinationUrl('')
        setShortUrl('')
        setDomain(`${defaultDomain}`)
        setUrlEnding('')
      }
      setErrors({destinationUrl: '', shortUrl: ''})
      setIsEditorOpen(false)
    }
  }, [isShow, initialData])

  useEffect(() => {
    if (userValues && userValues?.customShortLink?.status) {
      const customDomains = [
        {
          value: defaultDomain,
          label: defaultDomain,
        },
        {
          value: userValues?.customShortLink?.domain,
          label: userValues?.customShortLink?.domain,
        },
      ]

      const updated = [...domainOptions, ...customDomains]
      const distinct = updated.filter(
        (thing, index, self) => index === self.findIndex((t) => t.value === thing.value)
      )
      setDomainOptions(distinct)
    }
  }, [userValues])

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    const urlPattern =
      /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/
    return urlPattern.test(url)
  }

  // Validate short URL format (alphanumeric, 5-24 chars)
  const isValidShortUrl = (code: string): boolean => {
    return /^[a-zA-Z0-9]{3,24}$/.test(code)
  }

  const handleDestinationUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setDestinationUrl(value)

    if (value === '') {
      setErrors((prev) => ({...prev, destinationUrl: ''}))
    } else if (!isValidUrl(value)) {
      setErrors((prev) => ({...prev, destinationUrl: 'Input valid URL, like "example.co/goodurl"'}))
    } else {
      setErrors((prev) => ({...prev, destinationUrl: ''}))
    }
  }

  const handleShortUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setShortUrl(value)

    if (!isValidShortUrl(value)) {
      setErrors((prev) => ({
        ...prev,
        shortUrl:
          'Endsuffix is required min 3 characters. Please input proper suffix to create your link.',
      }))
    } else {
      setErrors((prev) => ({...prev, shortUrl: ''}))
    }
  }

  const handleDomainChange = (selectedOption: {value: string; label: string} | null) => {
    // Navigate to My Profile page when user selects to create a custom domain
    if (selectedOption?.value === 'create') {
      window.open('/accounts/settings', '_blank')
      return
    }
    const value = selectedOption ? selectedOption.value : defaultDomain
    setSelectedDomain(selectedOption || {value: '', label: defaultDomain})
    updateShortUrl(value, urlEnding)
  }

  const handleUrlEndingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setUrlEnding(value)

    if (!isValidShortUrl(value)) {
      setErrors((prev) => ({
        ...prev,
        shortUrl:
          'Endsuffix is required min 3 characters. Please input proper suffix to create your link.',
      }))
    } else {
      checkShortUrlDuplication(value)
    }
  }

  const updateShortUrl = (domainValue: string, endingValue: string) => {
    if (endingValue) {
      setShortUrl(`${endingValue}`)
    } else {
      setShortUrl('')
    }
  }

  const toggleEditor = () => {
    if (isSuccess) {
      openDestinationUrl()
    } else {
      setIsEditorOpen(!isEditorOpen)
      setSelectedDomain({value: '', label: defaultDomain})
      // If opening editor and urlEnding is empty, extract from shortUrl
      if (!isEditorOpen) {
        if (!urlEnding && shortUrl) {
          setUrlEnding(shortUrl)
        }
        // Focus on URL ending field after render
        setTimeout(() => urlEndingRef?.current?.focus(), 0)
      }

      checkShortUrlDuplication(shortUrl)
    }
  }

  const handleSaveEditor = () => {
    if (isValidShortUrl(urlEnding)) {
      setShortUrl(urlEnding)
      setDomain(defaultDomain)
      setIsEditorOpen(false)
    }
  }

  const handleCancelEditor = () => {
    setIsEditorOpen(false)
    // Reset to original values if needed
    setUrlEnding('')
    checkShortUrlDuplication(shortUrl)
  }

  const getRandomChars = (inputString: any, count: number) => {
    let result = ''

    if (count <= inputString?.length) {
      // Filter out special characters and keep only alphanumeric chars
      const validChars: string =
        inputString
          ?.split('')
          ?.filter((char: string) => /[a-zA-Z0-9]/.test(char))
          ?.join('') || ''

      // Use valid chars, or pad with 'x' if not enough
      let shuffled
      if (validChars.length < count) {
        shuffled = validChars.padEnd(count, 'x')
      } else {
        shuffled = validChars.slice(0, count)
      }
      result = shuffled
    }

    return result
  }

  const checkShortUrlDuplication = (shortUrl: string) => {
    const checkDuplicateShortUrl = allTrackingLinks?.find(
      (it: any) => it?.shortUrl?.toLowerCase() === shortUrl?.trim()
    )
    if (checkDuplicateShortUrl) {
      setErrors({...errors, shortUrl: 'Please enter Unique code '})
    } else {
      setErrors({...errors, shortUrl: ''})
    }
  }

  const handleDestinationUrlBlur = () => {
    const hostName = destinationUrl.match(/^(?:https?:\/\/)?(?:www\.)?([^:\/\n?]+)/)?.[1]
    const name = getRandomChars(hostName, 3)
    setShortUrl(name?.trim())

    checkShortUrlDuplication(name)
  }

  const handleAddShortUrl = () => {
    let startSubString = bulkMessageData?.message?.slice(0, inputRef.current?.selectionStart)
    let endSubString = bulkMessageData?.message?.slice(inputRef.current?.selectionEnd)

    // Use the effective domain - custom domain if enabled, otherwise default
    const effectiveDomain = userValues?.customShortLink?.status
      ? userValues.customShortLink?.domain
      : defaultDomain

    // send data to api
    thunkDispatch(
      CreateData({
        shortUrl: shortUrl,
        upstreamUrl: destinationUrl,
      })
    ).then((response) => {
      if (response.payload.status) {
        setIsSuccess(true)

        // Use shortUrl from API response if available, otherwise fall back to local state
        // Ensure we never use undefined - fall back to empty string as last resort
        const createdShortUrl =
          response.payload?.data?.shortUrl || response.payload?.data?.code || shortUrl || ''

        // Don't proceed if we don't have a valid shortUrl
        if (!createdShortUrl) {
          console.error('ShortUrlModal: No valid shortUrl available')
          return
        }

        if (initialData && initialData?.longUrl !== '') {
          let previousMessage = bulkMessageData?.message
          let addAgain =
            startSubString + `${effectiveDomain}/${initialData?.shortUrl}` + ' ' + endSubString
          const regex = /short\.cellcast\.com\/\w+/

          let finalDataOfLongUrl = [...(bulkMessageData?.longUrl || [])]?.map((key: any) => ({
            longUrl: key?.longUrl,
            shortUrl: key?.shortUrl,
          }))
          let longUrlData = bulkMessageData?.longUrl?.findIndex(
            (val: any) => val?.shortUrl === initialData?.shortUrl
          )

          finalDataOfLongUrl.splice(longUrlData, 1, {
            longUrl: destinationUrl,
            shortUrl: createdShortUrl,
          })

          if (previousMessage?.match(regex) == null) {
            setBulkMessageData({
              ...bulkMessageData,
              message: addAgain,
              longUrl: finalDataOfLongUrl,
            })
            if (isDraft) {
              dispatch(
                BulkMsgDraftData({
                  ...bulkMessageData,
                  message: addAgain,
                  longUrl: finalDataOfLongUrl,
                })
              )
            }
          } else {
            let content = previousMessage?.replace(
              new RegExp('\\b' + initialData?.shortUrl + '\\b'),
              createdShortUrl
            )
            setBulkMessageData({
              ...bulkMessageData,
              message: content,
              longUrl: finalDataOfLongUrl,
            })
            if (isDraft) {
              dispatch(
                BulkMsgDraftData({
                  ...bulkMessageData,
                  message: content,
                  longUrl: finalDataOfLongUrl,
                })
              )
            }
          }
        } else {
          let content =
            startSubString + `${effectiveDomain}/${createdShortUrl}` + ' ' + endSubString

          if (!bulkMessageData?.longUrl) {
            setBulkMessageData({
              ...bulkMessageData,
              message: content,
              longUrl: [{longUrl: destinationUrl, shortUrl: createdShortUrl}],
            })
          } else {
            bulkMessageData?.longUrl?.length < 4 &&
              setBulkMessageData({
                ...bulkMessageData,
                message: content,
                longUrl: [
                  ...bulkMessageData?.longUrl,
                  {longUrl: destinationUrl, shortUrl: createdShortUrl},
                ],
              })
          }

          if (isDraft) {
            dispatch(
              BulkMsgDraftData({
                ...bulkMessageData,
                message: content,
                longUrl: [
                  ...bulkMessageData?.longUrl,
                  {longUrl: destinationUrl, shortUrl: createdShortUrl},
                ],
              })
            )
          }
        }

        inputRef?.current?.focus()
      } else {
        setErrors({...errors, shortUrl: 'Please enter Unique code '})
      }
    })
  }

  const openDestinationUrl = () => {
    if (destinationUrl) {
      const url = destinationUrl.startsWith('http') ? destinationUrl : `https://${destinationUrl}`
      window.open(url, '_blank')
    }
  }

  return (
    <Modal id='kt_modal_create_short_url' show={isShow} onHide={handleClose} centered size='lg'>
      <Modal.Header className='border-0 d-flex justify-content-between'>
        <h5 style={{fontSize: '20px'}}>Add short URL</h5>
        <button
          type='button'
          className='btn-close'
          onClick={handleClose}
          aria-label='Close'
        ></button>
      </Modal.Header>

      <Modal.Body>
        {isSuccess ? (
          <div
            className='success-alert d-flex justify-content-center align-items-center text-center '
            role='alert'
          >
            🎉 Link successfully created!
          </div>
        ) : (
          <div className='title'>
            Enter your destination URL, and optionally personalize your short link by customizing
            the domain name or URL ending. Best practice{' '}
            <span>
              {' '}
              <Link
                to='/'
                style={{color: '#3E97FF', textDecoration: 'underline'}}
                target='_blank'
                rel='noopener noreferrer'
              >
                learn more
              </Link>
            </span>
          </div>
        )}
        <div className='mb-4'>
          <div className='mb-2 d-flex justify-content-between align-items-center'>
            <label className='form-label mb-0'>Destination URL</label>

            {!isSuccess && (
              <button
                className='btn btn-sm btn-link text-decoration-none p-0'
                disabled={!isValidUrl(destinationUrl)}
                style={{
                  color: isValidUrl(destinationUrl) ? '#3E97FF' : '#B0BAC9',
                }}
                onClick={openDestinationUrl}
              >
                Redirect{' '}
                <i
                  className='bi bi-box-arrow-up-right'
                  style={{
                    color: isValidUrl(destinationUrl) ? '#3E97FF' : '#B0BAC9',
                    marginLeft: '6px',
                  }}
                ></i>
              </button>
            )}
          </div>
          <input
            type='text'
            className={`form-control ${errors.destinationUrl ? 'is-invalid' : ''}`}
            placeholder='https://example.com/very-long-url'
            disabled={isSuccess}
            value={destinationUrl}
            onChange={handleDestinationUrlChange}
            onBlur={handleDestinationUrlBlur}
          />
          {errors.destinationUrl && (
            <div className='invalid-feedback d-block'>{errors.destinationUrl}</div>
          )}
        </div>

        <div className='mb-3'>
          <label className='form-label d-flex align-items-center mb-2'>
            Your short link <i className='bi bi-info-circle ms-2 text-muted'></i>
          </label>
          <div className='input-group'>
            <span className='input-group-text bg-light border-end-0'>
              {(userValues?.customShortLink?.status
                ? userValues.customShortLink?.domain
                : defaultDomain) + '/'}
            </span>
            <input
              type='text'
              className={`form-control border-start-0 ${
                !isEditorOpen && errors.shortUrl.toLowerCase().includes('unique')
                  ? 'is-invalid'
                  : ''
              }`}
              placeholder='endsuffix --XXXX'
              disabled={true}
              value={shortUrl}
              onChange={handleShortUrlChange}
              ref={shortUrlRef}
            />

            <button
              type='button'
              className='btn btn-outline-secondary text-primary'
              disabled={!!errors.shortUrl && !errors.shortUrl.toLowerCase().includes('unique')}
              onClick={toggleEditor}
            >
              {!isSuccess
                ? !!shortUrl &&
                  (!!errors.shortUrl || !errors.shortUrl.toLowerCase().includes('unique')) &&
                  !isEditorOpen
                  ? 'Edit '
                  : ''
                : 'Try the link'}
            </button>
            {!isEditorOpen && errors.shortUrl.toLowerCase().includes('unique') && (
              <div className='invalid-feedback'>{errors.shortUrl}</div>
            )}
          </div>
        </div>

        {isEditorOpen && (
          <>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center mb-2'>Domain</label>
                <div className='d-flex flex-row align-items-center w-100'>
                  <input
                    value={
                      userValues.customShortLink?.status
                        ? userValues.customShortLink?.domain
                        : defaultDomain
                    }
                    disabled={true}
                    className={`form-control w-100`}
                  />
                  <span className='fs-2 text-muted ms-2'>/</span>
                </div>
              </div>

              <div className='col-md-6'>
                <label className='form-label d-flex align-items-center mb-2'>
                  URL ending <i className='bi bi-info-circle ms-2 text-muted'></i>
                </label>
                <input
                  type='text'
                  className={`form-control ${errors.shortUrl ? 'is-invalid' : ''}`}
                  placeholder='CB2234'
                  value={urlEnding}
                  onChange={handleUrlEndingChange}
                  ref={urlEndingRef}
                />
                {errors.shortUrl && <div className='invalid-feedback'>{errors.shortUrl}</div>}
              </div>
            </div>

            <div className='container-color-button-edit d-flex justify-content-end gap-2 mb-3'>
              <button type='button' className='btn btn-sm rounded-1' onClick={handleCancelEditor}>
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-light-primary btn-sm rounded-1'
                onClick={handleSaveEditor}
                disabled={!urlEnding || !!errors.shortUrl}
              >
                Save
              </button>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className='border-0'>
        {isSuccess ? (
          <button type='button' className='btn btn-primary ms-auto rounded-1' onClick={handleClose}>
            Okay
          </button>
        ) : (
          <>
            <button type='button' className='btn btn-link ms-auto rounded-1' onClick={handleClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={
                !destinationUrl ||
                !shortUrl ||
                !!errors.destinationUrl ||
                !!errors.shortUrl ||
                isEditorOpen
              }
              onClick={handleAddShortUrl}
            >
              Add
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ShortUrlModal
